import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  List,
  Row,
  Space,
  Spin,
  Typography,
  BackTop,
} from "antd";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  LoadingOutlined,
  AppstoreOutlined
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";

const { Title, Text } = Typography;

const TestReports = () => {
  const { project: urlProject, timestamp: urlTimestamp } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(urlProject || null);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch available projects
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const token = await getTokenSilently();
        const response = await fetch(
          `${config.baseUrl[isProd ? "prod" : "dev"]}/test-reports`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setProjects(data.data.projects || []);
        } else {
          const errorText = await response.text();
          console.error("Failed to fetch projects:", response.status, errorText);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
      setLoading(false);
    };

    fetchProjects();
  }, [getTokenSilently, isProd]);

  // Fetch reports for selected project
  const fetchReports = useCallback(async (project, skip = 0, limit = 20) => {
    if (skip === 0) {
      setReportsLoading(true);
      setReports([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = await getTokenSilently();
      const params = new URLSearchParams({
        project,
        limit: limit.toString(),
        skip: skip.toString(),
      });

      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/test-reports?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newReports = data.data.reports || [];

        if (skip === 0) {
          setReports(newReports);
        } else {
          setReports(prev => [...prev, ...newReports]);
        }

        setHasMore(data.data.hasMore || false);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch reports:", response.status, errorText);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    }

    setReportsLoading(false);
    setLoadingMore(false);
  }, [getTokenSilently, isProd]);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setSelectedReport(null);
    history.push(`/test-reports/${project}`);
    fetchReports(project);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setReports([]);
    setSelectedReport(null);
    history.push('/test-reports');
  };

  const handleBackToReports = () => {
    setSelectedReport(null);
    if (selectedProject) {
      history.push(`/test-reports/${selectedProject}`);
    }
  };

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    if (selectedProject) {
      history.push(`/test-reports/${selectedProject}/${report.timestamp}`);
    }
  };

  const getReportUrl = (reportPath) => {
    const baseUrl = config.testReports[isProd ? "prod" : "dev"];
    return `${baseUrl}/${reportPath}`;
  };

  const handleLoadMore = () => {
    if (selectedProject && !loadingMore) {
      fetchReports(selectedProject, reports.length);
    }
  };

  // Handle URL parameters for deep linking
  useEffect(() => {
    if (urlProject) {
      // Set the project if it's different from URL
      if (selectedProject !== urlProject) {
        setSelectedProject(urlProject);
      }
      // Always fetch reports when we have a project in URL
      if (!reports.length && !reportsLoading) {
        fetchReports(urlProject);
      }
    }
  }, [urlProject, selectedProject, reports.length, reportsLoading, fetchReports]);

  // Handle timestamp in URL after reports are loaded
  useEffect(() => {
    if (urlProject && urlTimestamp && reports.length > 0) {
      const report = reports.find(r => r.timestamp === urlTimestamp);
      if (report && !selectedReport) {
        setSelectedReport(report);
      }
    }
  }, [urlProject, urlTimestamp, reports, selectedReport]);

  const formatTimestamp = (timestamp) => {
    // Parse UTC timestamp and format in user's local timezone
    const date = parseTimestamp(timestamp);
    
    // Format date in user's local timezone
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const parseTimestamp = (timestamp) => {
    // Convert timestamp like "06-18-25-15:32:32" to Date object (UTC)
    const parts = timestamp.split('-');
    if (parts.length >= 4) {
      const month = parseInt(parts[0], 10) - 1; // JavaScript months are 0-based
      const day = parseInt(parts[1], 10);
      const year = 2000 + parseInt(parts[2], 10);
      const timeParts = parts[3].split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = parseInt(timeParts[2], 10);
      // Create UTC date and return as local time
      return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    }
    return new Date();
  };

  const getRelativeTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 0) return 'just now'; // Handle future dates
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 2592000) { // 30 days
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }
    return formatTimestamp(timestamp);
  };

  const getTimestampColor = (timestamp) => {
    const date = parseTimestamp(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 86400) { // Less than 24 hours
      return '#52c41a'; // Green
    } else if (diffInSeconds < 604800) { // Less than 1 week
      return '#fa8c16'; // Orange
    } else {
      return '#8c8c8c'; // Gray
    }
  };

  // If viewing a specific report
  if (selectedReport) {
    return (
      <div style={{ margin: "16px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Breadcrumb>
            <Breadcrumb.Item>Test Reports</Breadcrumb.Item>
            <Breadcrumb.Item>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a nohref onClick={(e) => { e.preventDefault(); handleBackToProjects(); }}>Projects</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a nohref onClick={(e) => { e.preventDefault(); handleBackToReports(); }}>{selectedProject}</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{formatTimestamp(selectedReport.timestamp)}</Breadcrumb.Item>
          </Breadcrumb>

          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => window.open(getReportUrl(selectedReport.reportPath), '_blank')}
          >
            Open in New Tab
          </Button>
        </div>

        <iframe
          title={`Test Report - ${selectedProject} - ${formatTimestamp(selectedReport.timestamp)}`}
          src={getReportUrl(selectedReport.reportPath)}
          style={{
            marginTop: "0px",
            marginLeft: "-36px",
            overflow: "hidden",
            height: "calc(100vh - 90px)",
            width: "calc(100vw - 95px)",
          }}
          height="100%"
          width="100%"
        />
      </div>
    );
  }

  return (
    <div style={{ margin: "16px 16px" }}>
      <Breadcrumb style={{ marginBottom: "20px" }}>
        <Breadcrumb.Item>Test Reports</Breadcrumb.Item>
        <Breadcrumb.Item>
          {!selectedProject ? (
            "Projects"
          ) : (
            <>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" onClick={(e) => { e.preventDefault(); handleBackToProjects(); }}>Projects</a>
            </>
          )}
        </Breadcrumb.Item>
        {selectedProject && <Breadcrumb.Item>{selectedProject}</Breadcrumb.Item>}
      </Breadcrumb>

      <Divider />

      {!selectedProject ? (
        // Projects list view
        <>
          <Title level={3} style={{ marginBottom: "20px" }}>
            <Space>
              <FileTextOutlined />
              Test Reports Projects
            </Space>
          </Title>

          {loading ? (
            <div style={{ textAlign: "center", marginTop: "50px" }}>
              <Spin size="large" />
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {projects.map((project) => (
                <Col xs={12} sm={12} md={8} lg={6} key={project}>
                  <Card
                    hoverable
                    onClick={() => handleProjectSelect(project)}
                    style={{ 
                      textAlign: "center",
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <AppstoreOutlined style={{ 
                      fontSize: isMobile ? "36px" : "48px", 
                      color: "#1890ff", 
                      marginBottom: isMobile ? "8px" : "16px" 
                    }} />
                    <Title level={4} style={{ 
                      margin: 0,
                      fontSize: isMobile ? '16px' : '20px'
                    }}>
                      {project}
                    </Title>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {!loading && projects.length === 0 && (
            <Alert
              message="No Projects Found"
              description="No test report projects are available at the moment."
              type="info"
              showIcon
            />
          )}
        </>
      ) : (
        // Reports list view for selected project
        <>
          <div style={{ marginBottom: "20px" }}>
            <Title level={3} style={{ margin: 0 }}>
              <Space>
                <FileTextOutlined />
                {selectedProject} Test Reports
              </Space>
            </Title>
          </div>

          {reportsLoading ? (
            <div style={{ textAlign: "center", marginTop: "50px" }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <List
                itemLayout="horizontal"
                dataSource={reports}
                renderItem={(report) => (
                  <List.Item
                    actions={!isMobile ? [
                      <Button
                        type="primary"
                        onClick={() => handleReportSelect(report)}
                      >
                        View Report
                      </Button>,
                      <Button
                        icon={<LinkOutlined />}
                        onClick={() => window.open(getReportUrl(report.reportPath), '_blank')}
                      >
                        Open in New Tab
                      </Button>
                    ] : [
                      <Space direction="horizontal" size="small">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleReportSelect(report)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          icon={<LinkOutlined />}
                          onClick={() => window.open(getReportUrl(report.reportPath), '_blank')}
                        >
                          Open
                        </Button>
                      </Space>
                    ]}
                    style={{ cursor: "pointer", padding: "12px 0" }}
                    onClick={() => handleReportSelect(report)}
                  >
                    <List.Item.Meta
                      avatar={<ClockCircleOutlined style={{ fontSize: "24px", color: getTimestampColor(report.timestamp) }} />}
                      title={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <Text strong style={{ 
                            fontSize: isMobile ? '14px' : '16px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: isMobile ? '200px' : 'auto'
                          }}>
                            {formatTimestamp(report.timestamp)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: isMobile ? "12px" : "14px" }}>
                            {getRelativeTime(report.timestamp)}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />

              {hasMore && (
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <Button
                    type="primary"
                    loading={loadingMore}
                    onClick={handleLoadMore}
                    icon={loadingMore ? <LoadingOutlined /> : undefined}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </div>
              )}

              {reports.length === 0 && !reportsLoading && (
                <Alert
                  message="No Reports Found"
                  description={`No test reports are available for project "${selectedProject}".`}
                  type="info"
                  showIcon
                />
              )}
            </>
          )}
        </>
      )}

      <BackTop />
    </div>
  );
};

export default TestReports;